import { formatNumber } from '../utils/format';
import { BUILDING_MAP, BUILDING_ORDER } from '../constants/buildings';
import { getRaceUnitName, getUnitPointsLabel } from '../constants/game';
import type { ThieveryResult as ThieveryResultType } from '../types/thievery';
import { COLORS } from '../constants/theme';
import { SectionHeader } from './ui/SectionHeader';

interface ThieveryResultProps {
  result: ThieveryResultType;
}

export function ThieveryResult({ result }: ThieveryResultProps) {
  const isSuccess = result.success;

  // Extract operation-specific data
  const data = result.operationData as Record<string, unknown>;

  const bannerStyle = isSuccess
    ? { backgroundColor: COLORS.successBg, borderColor: COLORS.successBorder, color: COLORS.successText }
    : { backgroundColor: COLORS.errorBg, borderColor: COLORS.errorBorder, color: COLORS.errorText };

  return (
    <div
      className="border px-4 py-3 rounded text-sm"
      style={bannerStyle}
    >
      {/* SF-149 Component 5: role="alert"/aria-live scope narrowed to just the message + basic
          stats block below — the outer container used to wrap the entire result subtree
          (including every OperationData table), so a screen reader announced the full result
          body as one alert on every submit. OperationData is now a sibling, reached via normal
          linear reading order instead. */}
      <div role="alert" aria-live={isSuccess ? 'polite' : 'assertive'}>
        {/* Main Message */}
        <div className="font-medium">{result.message}</div>

        {/* Basic Stats */}
        <div className="mt-2 text-xs space-y-1">
          <div>Thieves Sent: {formatNumber(result.thievesSent)}</div>
          {result.thievesLost > 0 && (
            <div className="text-red-400">Thieves Lost: {formatNumber(result.thievesLost)}</div>
          )}
          {result.thievesConsumed > 0 && (
            <div>Thieves Consumed: {formatNumber(result.thievesConsumed)}</div>
          )}
          <div>Stealth Cost: {Math.round(result.stealthCost)}%</div>
          {result.identityRevealed && (
            <div className="font-medium" style={{ color: COLORS.warning }}>
              Your identity was revealed to the defender.
            </div>
          )}
        </div>
      </div>

      {/* Operation-Specific Data (only on success) */}
      {isSuccess && <OperationData operationName={result.operationName} data={data} />}
    </div>
  );
}

interface OperationDataProps {
  operationName: string;
  data: Record<string, unknown>;
}

function OperationData({ operationName, data }: OperationDataProps) {
  // Intel Operations
  if (operationName === 'SPY_ON_THRONE') {
    return <SpyOnThroneData data={data} />;
  }

  if (operationName === 'SPY_ON_MILITARY') {
    return <SpyOnMilitaryData data={data} />;
  }

  if (operationName === 'INFILTRATE') {
    return <InfiltrateData data={data} />;
  }

  if (operationName === 'SPY_ON_SCIENCE') {
    return <SpyOnScienceData data={data} />;
  }

  if (operationName === 'SURVEY') {
    return <SurveyData data={data} />;
  }

  if (operationName === 'SPY_ON_HOME_DEFENSE') {
    return <SpyOnHomeDefenseData data={data} />;
  }

  // Resource Operations
  if (operationName === 'ROB_THE_VAULTS' || operationName === 'ROB_THE_GRANARIES' || operationName === 'ROB_THE_TOWERS') {
    return <ResourceTheftData data={data} />;
  }

  if (operationName === 'KIDNAPPING') {
    return <KidnappingData data={data} />;
  }

  // Destructive Operations
  if (operationName === 'ARSON') {
    return <ArsonData data={data} />;
  }

  if (operationName === 'NIGHT_STRIKE') {
    return <NightStrikeData data={data} />;
  }

  if (operationName === 'SABOTAGE_WIZARDS') {
    return <SabotageWizardsData data={data} />;
  }

  // Duration Operations
  if (operationName === 'INCITE_RIOTS') {
    return <InciteRiotsData data={data} />;
  }

  return null;
}

// Intel operation result components
function SpyOnThroneData({ data }: { data: Record<string, unknown> }) {
  const ruler = data.ruler as string | undefined;
  const race = data.race as string | undefined;
  const thieves = data.thieves as number | undefined;
  const wizards = data.wizards as number | undefined;
  const manaRating = data.manaRating as number | undefined;
  const netIncomePerTick = data.netIncomePerTick as number | undefined;

  // Build rows for the intel table
  const rows: Array<{ category: string; label: string; value: string }> = [];

  if (ruler !== undefined) {
    rows.push({ category: 'Identity', label: 'Ruler', value: ruler });
  }
  if (race !== undefined) {
    rows.push({ category: 'Identity', label: 'Race', value: race.charAt(0) + race.slice(1).toLowerCase() });
  }
  if (thieves !== undefined) {
    rows.push({ category: 'Military', label: 'Thieves', value: formatNumber(thieves) });
  }
  if (wizards !== undefined) {
    rows.push({ category: 'Magic', label: 'Wizards', value: formatNumber(wizards) });
  }
  if (manaRating !== undefined) {
    rows.push({ category: 'Magic', label: 'Mana Rating', value: `${(manaRating * 100).toFixed(1)}%` });
  }
  if (netIncomePerTick !== undefined) {
    rows.push({ category: 'Economy', label: 'Net Income/day', value: `${formatNumber(Math.round(netIncomePerTick))} gc` });
  }

  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: `1px solid ${COLORS.successBorder}` }}
    >
      {/* Section Title */}
      <SectionHeader title="Throne Room Intelligence" />

      {/* Intel Table */}
      <div
        className="border border-t-0 rounded-b overflow-hidden overflow-x-auto"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
              <th className="py-1.5 px-3 text-left font-medium">Category</th>
              <th className="py-1.5 px-3 text-left font-medium">Item</th>
              <th className="py-1.5 px-3 text-right font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={`${row.category}-${row.label}`}
                style={{ backgroundColor: idx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card }}
              >
                <td className="py-1.5 px-3" style={{ color: COLORS.text.tertiary }}>{row.category}</td>
                <td className="py-1.5 px-3" style={{ color: COLORS.text.primary }}>{row.label}</td>
                <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatScalarPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function SpyOnMilitaryData({ data }: { data: Record<string, unknown> }) {
  const troopsAtHome = data.troopsAtHome as Record<string, number | null> | undefined;
  const training = data.training as { hasQueue: boolean; queueLength: number; nextCompletionTicks?: number | null } | undefined;
  const armiesOut = data.armiesOut as { hasArmies: boolean; armyCount: number; nextReturnTicks?: number | null } | undefined;
  const offensiveME = data.offensiveME as number | undefined;
  const defensiveME = data.defensiveME as number | undefined;
  const offensePoints = data.offensePoints as number | undefined;
  const defensePoints = data.defensePoints as number | undefined;
  const race = data.race as string | undefined;

  // Build army unit rows with race-specific names and points
  const armyUnits = [
    { id: 'generals', label: 'Generals', points: '' },
    { id: 'soldiers', label: `Soldiers ${getUnitPointsLabel(race, 'soldier')}`, points: '' },
    { id: 'offensiveSpecs', label: `${getRaceUnitName(race, 'offense')} ${getUnitPointsLabel(race, 'offense')}`, points: '' },
    { id: 'defensiveSpecs', label: `${getRaceUnitName(race, 'defense')} ${getUnitPointsLabel(race, 'defense')}`, points: '' },
    { id: 'elites', label: `${getRaceUnitName(race, 'elite')} ${getUnitPointsLabel(race, 'elite')}`, points: '' },
  ];

  const trainingUnitTypes = [
    `${getRaceUnitName(race, 'offense')} ${getUnitPointsLabel(race, 'offense')}`,
    `${getRaceUnitName(race, 'defense')} ${getUnitPointsLabel(race, 'defense')}`,
    `${getRaceUnitName(race, 'elite')} ${getUnitPointsLabel(race, 'elite')}`,
    'Thieves',
  ];

  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: `1px solid ${COLORS.successBorder}` }}
    >
      {/* Section Title */}
      <SectionHeader title="Military Intelligence" />

      {/* Intro and Effectiveness */}
      <div
        className="border border-t-0 p-4 text-sm mb-4"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border, color: COLORS.text.secondary }}
      >
        <p className="mb-4">
          Your thieves have gathered intelligence on the target province's military forces.
          This report details their troops at home, training activity, and military efficiency.
        </p>

        <p className="font-medium mb-2" style={{ color: COLORS.text.primary }}>Military Effectiveness</p>
        <p className="mb-3">
          Our military effectiveness is determined by the wage rates of our armed forces. Higher
          wages can drive up productivity. Any changes to pay will take time to change our
          efficiency.
        </p>
        <div className="rounded p-3 text-center" style={{ backgroundColor: COLORS.sectionHeader }}>
          <div className="mb-1">
            Offensive Military Effectiveness:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {offensiveME != null ? formatScalarPercent(offensiveME) : <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>}
            </span>
          </div>
          <div className="mb-1">
            Defensive Military Effectiveness:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {defensiveME != null ? formatScalarPercent(defensiveME) : <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>}
            </span>
          </div>
          <div className="mb-1">
            Net Offensive Points at Home:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {offensePoints != null ? formatNumber(Math.round(offensePoints)) : <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>}
            </span>
          </div>
          <div>
            Net Defensive Points at Home:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {defensePoints != null ? formatNumber(Math.round(defensePoints)) : <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>}
            </span>
          </div>
        </div>
      </div>

      {/* Army Table */}
      <div
        className="border rounded overflow-hidden overflow-x-auto mb-4"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
              <th className="py-2 px-3 text-left font-medium">Army Number:</th>
              <th className="py-2 px-3 text-center font-medium">Standing</th>
            </tr>
          </thead>
          <tbody>
            {armyUnits.map((unit, idx) => {
              const unitData = troopsAtHome?.[unit.id];
              return (
                <tr
                  key={unit.id}
                  className="border-t"
                  style={{ borderColor: COLORS.border, backgroundColor: idx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card }}
                >
                  <td
                    className="py-1.5 px-3 font-medium"
                    style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}
                  >
                    {unit.label}
                  </td>
                  <td className="py-1.5 px-3 text-center" style={{ color: COLORS.text.primary }}>
                    {unitData != null ? formatNumber(unitData) : 'Unknown'}
                  </td>
                </tr>
              );
            })}
            <tr
              className="border-t"
              style={{ borderColor: COLORS.border, backgroundColor: COLORS.tableRowAlt }}
            >
              <td
                className="py-1.5 px-3 font-medium"
                style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}
              >
                Offensive Pts
              </td>
              <td className="py-1.5 px-3 text-center" style={{ color: COLORS.text.primary }}>
                {offensePoints != null ? formatNumber(Math.round(offensePoints)) : <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>}
              </td>
            </tr>
            <tr
              className="border-t"
              style={{ borderColor: COLORS.border, backgroundColor: COLORS.card }}
            >
              <td
                className="py-1.5 px-3 font-medium"
                style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}
              >
                Defensive Pts
              </td>
              <td className="py-1.5 px-3 text-center" style={{ color: COLORS.text.primary }}>
                {defensePoints != null ? formatNumber(Math.round(defensePoints)) : <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Training Queue - 24-tick grid */}
      <div
        className="border rounded overflow-hidden overflow-x-auto"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
              <th className="py-2 px-2 text-left font-medium sticky left-0" style={{ backgroundColor: COLORS.sectionHeader }}>
                Army Unit
              </th>
              {Array.from({ length: 24 }, (_, i) => (
                <th key={i} className="py-2 px-1 text-center font-medium min-w-[32px]">
                  {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trainingUnitTypes.map((unit, rowIdx) => (
              <tr
                key={unit}
                className="border-t"
                style={{ borderColor: COLORS.border, backgroundColor: rowIdx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card }}
              >
                <td className="py-1.5 px-2 font-medium sticky left-0 bg-inherit" style={{ color: COLORS.text.primary }}>
                  {unit}
                </td>
                {Array.from({ length: 24 }, (_, day) => (
                  <td key={day} className="py-1.5 px-1 text-center" style={{ color: COLORS.text.primary }}>
                    -
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {(training?.hasQueue || armiesOut?.hasArmies) && (
          <div
            className="px-3 py-2 text-xs border-t"
            style={{ color: COLORS.text.tertiary, borderColor: COLORS.border }}
          >
            {training?.hasQueue && (
              <div>
                Intelligence indicates {training.queueLength} unit{training.queueLength !== 1 ? 's' : ''} in training
                {training.nextCompletionTicks != null && (
                  <> (fully trained in {training.nextCompletionTicks} days)</>
                )}
              </div>
            )}
            {armiesOut?.hasArmies && (
              <div>
                {armiesOut.armyCount} arm{armiesOut.armyCount > 1 ? 'ies' : 'y'} currently deployed
                {armiesOut.nextReturnTicks != null && (
                  <> (earliest return: {armiesOut.nextReturnTicks} days)</>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfiltrateData({ data }: { data: Record<string, unknown> }) {
  const thieves = data.thieves as number | undefined;
  const rawTPA = data.rawTPA as number | undefined;

  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: `1px solid ${COLORS.successBorder}` }}
    >
      {/* Section Title */}
      <SectionHeader title="Thieves Guild Intelligence" />

      <div
        className="border border-t-0 rounded-b overflow-hidden overflow-x-auto"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
              <th className="py-1.5 px-3 text-left font-medium">Metric</th>
              <th className="py-1.5 px-3 text-right font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {thieves !== undefined && (
              <tr style={{ backgroundColor: COLORS.tableRowAlt }}>
                <td className="py-1.5 px-3" style={{ color: COLORS.text.primary }}>Thieves</td>
                <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>
                  {formatNumber(thieves)}
                </td>
              </tr>
            )}
            {rawTPA !== undefined && (
              <tr style={{ backgroundColor: COLORS.card }}>
                <td className="py-1.5 px-3" style={{ color: COLORS.text.primary }}>Raw TPA</td>
                <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>
                  {rawTPA.toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Sciences in classic Utopia order
const SCIENCE_ORDER = [
  { id: 'ALCHEMY', name: 'Alchemy', description: 'Increases gold income' },
  { id: 'TOOLS', name: 'Tools', description: 'Increases building efficiency' },
  { id: 'HOUSING', name: 'Housing', description: 'Increases max population' },
  { id: 'FOOD', name: 'Food', description: 'Increases food production' },
  { id: 'MILITARY', name: 'Military', description: 'Increases military effectiveness' },
  { id: 'THIEVERY', name: 'Crime', description: 'Improves thievery operations' },
  { id: 'CHANNELING', name: 'Channeling', description: 'Improves magic effectiveness' },
];

function formatResearchRate(rate: string): string {
  switch (rate.toUpperCase()) {
    case 'NONE': case 'NO_RESEARCH': return 'None (0%)';
    case 'MINIMAL': return 'Minimal (10%)';
    case 'LOW': return 'Low (25%)';
    case 'MODERATE': return 'Moderate (50%)';
    case 'HIGH': return 'High (75%)';
    case 'MAXIMUM': return 'Maximum (100%)';
    default: return rate.replace(/_/g, ' ');
  }
}

function SpyOnScienceData({ data }: { data: Record<string, unknown> }) {
  const researchRate = data.researchRate as string | undefined;
  const sciences = data.sciences as Array<{ scienceType: string; books: number; modifier: number }> | undefined;
  // Build lookup for display order
  const scienceMap = new Map(sciences?.map((s) => [s.scienceType, s]) ?? []);

  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: `1px solid ${COLORS.successBorder}` }}
    >
      {/* Section Title */}
      <SectionHeader title="Arts & Sciences" />

      {/* Intro + Research Rate Info */}
      <div
        className="border border-t-0 p-4 text-sm mb-4"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border, color: COLORS.text.secondary }}
      >
        <p className="mb-4">
          Your thieves have investigated the target province's scientific progress. This report
          details their research rate and the books of knowledge they have accumulated.
        </p>

        {researchRate && (
          <div className="rounded p-3" style={{ backgroundColor: COLORS.sectionHeader }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <div className="flex justify-between">
                <span style={{ color: COLORS.text.tertiary }}>Research Rate:</span>
                <span style={{ color: COLORS.text.primary }}>{formatResearchRate(researchRate)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Science Progress Table */}
      {sciences && sciences.length > 0 && (
        <div
          className="border rounded overflow-hidden overflow-x-auto"
          style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
                <th className="py-2 px-3 text-left font-medium">Technology</th>
                <th className="py-2 px-3 text-right font-medium">Books</th>
                <th className="py-2 px-3 text-right font-medium">Effect</th>
              </tr>
            </thead>
            <tbody>
              {SCIENCE_ORDER.map((science, idx) => {
                const scienceData = scienceMap.get(science.id);
                if (!scienceData) return null;
                return (
                  <tr
                    key={science.id}
                    className="border-t"
                    style={{ borderColor: COLORS.border, backgroundColor: idx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card }}
                  >
                    <td className="py-1.5 px-3">
                      <div className="font-medium" style={{ color: COLORS.text.primary }}>{science.name}</div>
                      <div className="text-xs" style={{ color: COLORS.text.muted }}>{science.description}</div>
                    </td>
                    <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>
                      {formatNumber(scienceData.books)}
                    </td>
                    <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>
                      {(scienceData.modifier * 100).toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Helper to get building name from BUILDING_MAP or format the ID
function getBuildingName(buildingId: string): string {
  if (buildingId === 'NONE') return 'Barren Lands';
  return BUILDING_MAP[buildingId]?.name || buildingId.replace(/_/g, ' ');
}

// Helper to build construction queue by tick (matching InternalAffairs)
function buildSurveyConstructionQueue(
  construction: Array<{ building: string; acres: number; ticksRemaining: number }> | undefined,
  maxTicks: number
): Record<number, Record<string, number>> {
  const queue: Record<number, Record<string, number>> = {};

  if (!construction) return queue;

  for (const item of construction) {
    const tick = item.ticksRemaining;
    if (tick > 0 && tick <= maxTicks) {
      const tickIndex = tick - 1;
      if (!queue[tickIndex]) {
        queue[tickIndex] = {};
      }
      const building = item.building;
      queue[tickIndex][building] = (queue[tickIndex][building] || 0) + item.acres;
    }
  }

  return queue;
}

function SurveyData({ data }: { data: Record<string, unknown> }) {
  const totalLand = data.totalLand as number | undefined;
  const barrenLand = data.barrenLand as number | undefined;
  const buildings = data.buildings as Record<string, number> | undefined;
  const construction = data.construction as Array<{ building: string; acres: number; ticksRemaining: number }> | undefined;

  const totalLandValue = totalLand ?? 0;
  const constructionByTick = buildSurveyConstructionQueue(construction, 24);

  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: `1px solid ${COLORS.successBorder}` }}
    >
      {/* Section Title */}
      <SectionHeader title="Survey Results" />

      {/* Intro and Effectiveness */}
      <div
        className="border border-t-0 p-4 text-sm mb-4"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border, color: COLORS.text.secondary }}
      >
        <p className="mb-4">
          Your thieves have surveyed the target province's infrastructure. This intelligence report
          shows their buildings, land distribution, and any ongoing construction projects.
        </p>

        <p className="font-medium mb-2" style={{ color: COLORS.text.primary }}>Building Effectiveness</p>
        <p className="mb-3">
          The structures constructed throughout the target's lands may or may not function at full
          capacity. Our thieves were unable to determine their workforce or building efficiency.
        </p>
        <div className="rounded p-3" style={{ backgroundColor: COLORS.sectionHeader }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Current Available Workers:</span>
              <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Building Efficiency:</span>
              <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Current Available Jobs:</span>
              <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Workers Needed for Max:</span>
              <span className="italic" style={{ color: COLORS.text.tertiary }}>Unknown</span>
            </div>
          </div>
        </div>
      </div>

      {/* Building Effects Table */}
      <div
        className="border rounded overflow-hidden overflow-x-auto mb-4"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <div
          className="text-center py-1.5 text-sm font-medium"
          style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}
        >
          Building Effects
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
              <th className="py-2 px-3 text-left font-medium w-32">Building</th>
              <th className="py-2 px-3 text-right font-medium w-16">Owned</th>
              <th className="py-2 px-3 text-right font-medium w-12">%</th>
              <th className="py-2 px-3 text-left font-medium">Effect</th>
            </tr>
          </thead>
          <tbody>
            {BUILDING_ORDER.map((buildingId, idx) => {
              const owned = buildingId === 'NONE'
                ? (barrenLand ?? 0)
                : (buildings?.[buildingId] ?? 0);
              const percent = totalLandValue > 0 ? ((owned / totalLandValue) * 100).toFixed(1) : '0.0';
              const name = getBuildingName(buildingId);

              return (
                <tr
                  key={buildingId}
                  className="border-t"
                  style={{ borderColor: COLORS.border, backgroundColor: idx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card }}
                >
                  <td className="py-1.5 px-3 font-medium w-32" style={{ color: COLORS.text.primary }}>{name}</td>
                  <td className="py-1.5 px-3 text-right w-16" style={{ color: COLORS.text.primary }}>{formatNumber(owned)}</td>
                  <td className="py-1.5 px-3 text-right w-12" style={{ color: COLORS.text.primary }}>{percent}%</td>
                  <td className="py-1.5 px-3 text-left italic" style={{ color: COLORS.text.tertiary }}>-</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Exploration / Construction Queue */}
      <div
        className="border rounded overflow-hidden overflow-x-auto"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <div
          className="text-center py-1.5 text-sm font-medium"
          style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}
        >
          Exploration / Construction: Utopian Hours
        </div>
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
              <th
                className="py-2 px-2 text-left font-medium sticky left-0"
                style={{ backgroundColor: COLORS.sectionHeader }}
              >
                Building
              </th>
              <th className="py-2 px-2 text-right font-medium">Owned</th>
              <th className="py-2 px-2 text-right font-medium">%</th>
              {Array.from({ length: 24 }, (_, i) => (
                <th key={i} className="py-2 px-1 text-center font-medium min-w-[32px]">
                  {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BUILDING_ORDER.map((buildingId, rowIdx) => {
              const owned = buildingId === 'NONE'
                ? (barrenLand ?? 0)
                : (buildings?.[buildingId] ?? 0);
              const percent = totalLandValue > 0 ? ((owned / totalLandValue) * 100).toFixed(1) : '0.0';
              const name = getBuildingName(buildingId);

              return (
                <tr
                  key={buildingId}
                  className="border-t"
                  style={{ borderColor: COLORS.border, backgroundColor: rowIdx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card }}
                >
                  <td className="py-1.5 px-2 font-medium sticky left-0 bg-inherit" style={{ color: COLORS.text.primary }}>
                    {name}
                  </td>
                  <td className="py-1.5 px-2 text-right" style={{ color: COLORS.text.primary }}>{formatNumber(owned)}</td>
                  <td className="py-1.5 px-2 text-right" style={{ color: COLORS.text.primary }}>{percent}%</td>
                  {Array.from({ length: 24 }, (_, tick) => {
                    const count = constructionByTick[tick]?.[buildingId] ?? 0;
                    return (
                      <td key={tick} className="py-1.5 px-1 text-center" style={{ color: COLORS.text.primary }}>
                        {count > 0 ? formatNumber(count) : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SpyOnHomeDefenseData({ data }: { data: Record<string, unknown> }) {
  const homeDefense = data.homeDefense as number | undefined;

  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: `1px solid ${COLORS.successBorder}` }}
    >
      <SectionHeader title="Home Defense Intelligence" />

      <div
        className="border border-t-0 rounded-b overflow-hidden overflow-x-auto mb-2"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: COLORS.sectionHeader, color: COLORS.text.primary }}>
              <th className="py-1.5 px-3 text-left font-medium">Metric</th>
              <th className="py-1.5 px-3 text-right font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {homeDefense !== undefined && (
              <tr style={{ backgroundColor: COLORS.tableRowAlt }}>
                <td className="py-1.5 px-3" style={{ color: COLORS.text.primary }}>Total Home Defense</td>
                <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>
                  {formatNumber(Math.round(homeDefense))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs" style={{ color: COLORS.text.tertiary }}>100% accuracy</div>
    </div>
  );
}

// Resource operation result components
function ResourceTheftData({ data }: { data: Record<string, unknown> }) {
  const resourceStolen = data.resourceStolen as number | undefined;
  const atWar = data.atWar as boolean | undefined;

  return (
    <div className="mt-2 text-xs">
      {resourceStolen !== undefined && (
        <div className="text-yellow-400">Resources Stolen: {formatNumber(resourceStolen)}</div>
      )}
      {atWar && <div style={{ color: COLORS.text.tertiary }}>(War bonus applied)</div>}
    </div>
  );
}

function KidnappingData({ data }: { data: Record<string, unknown> }) {
  const peasantsCaptured = data.peasantsCaptured as number | undefined;
  const atWar = data.atWar as boolean | undefined;

  return (
    <div className="mt-2 text-xs">
      {peasantsCaptured !== undefined && (
        <div className="text-yellow-400">Peasants Captured: {formatNumber(peasantsCaptured)}</div>
      )}
      {atWar && <div style={{ color: COLORS.text.tertiary }}>(War bonus applied)</div>}
    </div>
  );
}

// Destructive operation result components
function ArsonData({ data }: { data: Record<string, unknown> }) {
  const buildingsDestroyed = data.buildingsDestroyed as number | undefined;

  return (
    <div className="mt-2 text-xs text-orange-400">
      {buildingsDestroyed !== undefined && (
        <div>Buildings Destroyed: {formatNumber(buildingsDestroyed)} acres</div>
      )}
    </div>
  );
}

function NightStrikeData({ data }: { data: Record<string, unknown> }) {
  const totalDead = data.totalDead as number | undefined;
  const soldiersDead = data.soldiersDead as number | undefined;
  const defensiveSpecsDead = data.defensiveSpecsDead as number | undefined;
  const offensiveSpecsDead = data.offensiveSpecsDead as number | undefined;
  const elitesDead = data.elitesDead as number | undefined;

  return (
    <div className="mt-2 text-xs space-y-1">
      {totalDead !== undefined && (
        <div className="text-red-400">Total Casualties: {formatNumber(totalDead)}</div>
      )}
      {soldiersDead !== undefined && soldiersDead > 0 && (
        <div>Soldiers: {formatNumber(soldiersDead)}</div>
      )}
      {defensiveSpecsDead !== undefined && defensiveSpecsDead > 0 && (
        <div>Defensive Specs: {formatNumber(defensiveSpecsDead)}</div>
      )}
      {offensiveSpecsDead !== undefined && offensiveSpecsDead > 0 && (
        <div>Offensive Specs: {formatNumber(offensiveSpecsDead)}</div>
      )}
      {elitesDead !== undefined && elitesDead > 0 && (
        <div>Elites: {formatNumber(elitesDead)}</div>
      )}
    </div>
  );
}

function SabotageWizardsData({ data }: { data: Record<string, unknown> }) {
  const manaReduction = data.manaReduction as number | undefined;

  return (
    <div className="mt-2 text-xs text-purple-400">
      {manaReduction !== undefined && (
        <div>Mana Reduced: {Math.round(manaReduction)}%</div>
      )}
    </div>
  );
}

// Duration operation result components
function InciteRiotsData({ data }: { data: Record<string, unknown> }) {
  const effectType = data.effectType as string | undefined;
  const duration = data.duration as number | undefined;
  const appliedAtTick = data.appliedAtTick as number | undefined;

  return (
    <div className="mt-2 text-xs">
      {effectType && duration && (
        <div className="text-orange-400">
          Effect: {effectType} for {duration} days
        </div>
      )}
      {appliedAtTick !== undefined && (
        <div style={{ color: COLORS.text.tertiary }}>Applied on day: {appliedAtTick}</div>
      )}
    </div>
  );
}
